import type { InterdictionResult } from "@jpjj/solver-core/interdict";
import { solveInterdiction } from "@jpjj/solver-core";
import { LEVELS } from "../game/levels";
import { state } from "../game/state";
import { drawMaze, pathUnder, reqFor } from "../game/grid";
import { $ } from "../dom";

/* ---------- Per-labyrinth solves (layouts are fixed, so cache them) ---------- */
const solvePromises: (Promise<InterdictionResult> | null)[] = LEVELS.map(() => null);
const solveResults: (InterdictionResult | null)[] = LEVELS.map(() => null);

function solveLevel(i: number): Promise<InterdictionResult> {
  let pr = solvePromises[i];
  if (!pr) {
    pr = solveInterdiction(reqFor(LEVELS[i])).then((res) => { solveResults[i] = res; return res; });
    solvePromises[i] = pr;
  }
  return pr;
}

/** Kick off all five solves during play, so the reveal is instant. */
export function prepareSolves(): Promise<InterdictionResult[]> {
  return Promise.all(LEVELS.map((_, i) => solveLevel(i)));
}

/* ---------- Carousel ---------- */
let cmpIndex = 0;
let animCancel = false;
let animRunning = false;
let timers: number[] = [];
const sleep = (ms: number) =>
  new Promise<void>((res) => { const t = window.setTimeout(res, ms); timers.push(t); });
function clearTimers(): void { for (const t of timers) clearTimeout(t); timers = []; }

export function openReveal(): void {
  $("solverOverlay").classList.remove("hidden");
  $("stageView").style.display = "";
  $("finalView").style.display = "none";
  cmpIndex = 0;
  void runLevelCompare(0);
}

async function runLevelCompare(i: number): Promise<void> {
  clearTimers();
  animCancel = false;
  animRunning = true;

  const L = LEVELS[i];
  const rec = state.records[i];
  $("cmpHead").innerHTML = `Labyrinth ${i + 1} / ${LEVELS.length} — <span class="lv-mon">🗝️ ${L.name}</span>`;
  ($("cmpNext") as HTMLButtonElement).disabled = true;
  $("cmpGap").innerHTML = "&nbsp;";
  $("optScore").textContent = "—";

  // your side renders immediately from your best recorded barrier
  const youSp = pathUnder(L, rec.walls);
  drawMaze($("youMaze"), L, rec.walls, youSp ? youSp.path : [], { kind: "you" });
  $("youScore").textContent = String(rec.length || L.base);

  const status = $("solverStatus");
  status.textContent = `Searching every wall placement for Labyrinth ${i + 1}…`;
  // show the empty maze on the optimizer side while it thinks
  drawMaze($("optMaze"), L, [], pathUnder(L, [])?.path ?? [], { kind: "opt" });

  let res: InterdictionResult;
  try {
    res = await solveLevel(i);
  } catch {
    status.textContent = "The exact solver could not be started — reload and try again.";
    animRunning = false;
    return;
  }
  if (animCancel) return;
  status.textContent = res.status === "OPTIMAL"
    ? `The solver proved the longest forced path in ${res.tSolveMs.toFixed(1)} ms — no walls can do better.`
    : `Best walls found in ${res.tSolveMs.toFixed(1)} ms (not proven best).`;

  await animateOptimal(i, res);
  if (animCancel) return;

  finishLevel(rec.length || L.base, res);
}

/** Raise the solver's walls one by one, the forced path lengthening with each. */
async function animateOptimal(levelIdx: number, res: InterdictionResult): Promise<void> {
  const L = LEVELS[levelIdx];
  for (let w = 0; w <= res.walls.length; w++) {
    if (animCancel) return;
    const partial = res.walls.slice(0, w);
    const sp = pathUnder(L, partial);
    drawMaze($("optMaze"), L, partial, sp ? sp.path : [], { kind: "opt" });
    $("optScore").textContent = String(sp ? sp.length : L.base);
    await sleep(520);
  }
}

function finishLevel(youLength: number, res: InterdictionResult): void {
  animRunning = false;
  const proven = res.status === "OPTIMAL";
  const gap = res.length - youLength;
  const gapEl = $("cmpGap");
  if (gap <= 0) {
    gapEl.textContent = proven
      ? "You forced the longest path there is — a perfect labyrinth."
      : "You matched (or beat) the best the solver found.";
  } else {
    const pct = ((gap / res.length) * 100).toFixed(0);
    gapEl.textContent =
      `Your maze: ${youLength} steps · ${proven ? "proven longest" : "solver's best"}: ` +
      `${res.length} steps — the hero escaped ${gap} step${gap === 1 ? "" : "s"} sooner than it had to (${pct}%).`;
  }
  const next = $("cmpNext") as HTMLButtonElement;
  next.disabled = false;
  next.textContent = cmpIndex === LEVELS.length - 1 ? "See your run ▸" : "Next labyrinth ▸";
}

export function skipCompare(): void {
  if (!animRunning) return;
  animCancel = true;
  clearTimers();
  const res = solveResults[cmpIndex];
  const rec = state.records[cmpIndex];
  const L = LEVELS[cmpIndex];
  if (!res) return;
  const sp = pathUnder(L, res.walls);
  drawMaze($("optMaze"), L, res.walls, sp ? sp.path : [], { kind: "opt" });
  $("optScore").textContent = String(res.length);
  finishLevel(rec.length || L.base, res);
}

export function nextCompare(): void {
  if (animRunning) { skipCompare(); return; }
  if (cmpIndex < LEVELS.length - 1) {
    cmpIndex++;
    void runLevelCompare(cmpIndex);
  } else {
    showFinal();
  }
}

function showFinal(): void {
  const youTotal = state.records.reduce((s, r, i) => s + (r.length || LEVELS[i].base), 0);
  const optTotal = solveResults.reduce((s, r, i) => s + (r ? r.length : LEVELS[i].base), 0);
  const baseTotal = LEVELS.reduce((s, L) => s + L.base, 0);
  const allProven = solveResults.every((r) => r && r.status === "OPTIMAL");
  $("fcYou").textContent = String(youTotal);
  $("fcOpt").textContent = String(optTotal);
  $("fcSub").textContent = allProven ? "steps · proven longest" : "steps · best found";
  const gap = optTotal - youTotal;
  $("fcGap").textContent = gap <= 0
    ? "A flawless dungeon — on every labyrinth you forced the longest path the walls allow."
    : `Your five labyrinths forced ${youTotal} steps in total; the optimizer forces ${optTotal} ` +
      `(the hero would otherwise stroll ${baseTotal}). Same walls, ${gap} step${gap === 1 ? "" : "s"} you left on the table.`;

  const rows = LEVELS.map((L, i) => {
    const o = solveResults[i];
    const ov = o ? o.length : L.base;
    const yv = state.records[i].length || L.base;
    const left = ov - yv;
    return `<tr><td>🗝️ ${L.name}</td><td>${yv}</td><td class="gold">${ov}</td>` +
      `<td>${left <= 0 ? "—" : "−" + left}</td></tr>`;
  }).join("");
  $("fcTable").innerHTML =
    `<thead><tr><th>Labyrinth</th><th>You</th><th>Longest</th><th>Left</th></tr></thead><tbody>${rows}</tbody>`;

  $("stageView").style.display = "none";
  $("finalView").style.display = "";
}

export function resetReveal(): void {
  animCancel = true;
  animRunning = false;
  clearTimers();
  cmpIndex = 0;
  $("youMaze").innerHTML = "";
  $("optMaze").innerHTML = "";
  $("stageView").style.display = "";
  $("finalView").style.display = "none";
}
